import { useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';

const REPORTS = [
  { key: 'claim-status', label: 'Claim Status Report' },
  { key: 'claim-report', label: 'Claim Report (by Group/Claim/Sub-Claim)' },
] as const;

export default function ClaimReports() {
  const [active, setActive] = useState<(typeof REPORTS)[number]>(REPORTS[0]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [groupBy, setGroupBy] = useState('GROUP');
  const [mode, setMode] = useState('DETAIL');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    if (active.key === 'claim-report') {
      return { fromDate: fromDate || undefined, toDate: toDate || undefined, groupBy, mode };
    }
    return { fromDate: fromDate || undefined, toDate: toDate || undefined };
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ecs/reports/${active.key}`, { params: buildParams() });
      setRows(res.data.rows || []);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const res = await api.get(`/ecs/reports/${active.key}`, { params: { ...buildParams(), format: 'excel' }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.key}.xlsx`;
    a.click();
  };

  const columns: Column<any>[] = rows.length > 0 ? Object.keys(rows[0]).map((k) => ({ key: k, header: k.replace(/_/g, ' ').toUpperCase() })) : [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Claim Reports</h1>
      <div className="flex gap-2 mb-4 border-b overflow-x-auto">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => { setActive(r); setRows([]); }}
            className={`px-4 py-2 text-sm whitespace-nowrap ${active.key === r.key ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex gap-4 items-end mb-4 flex-wrap">
        <Input label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <Input label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        {active.key === 'claim-report' && (
          <>
            <Select
              label="Group By"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              options={[
                { value: 'GROUP', label: 'Claim Group' },
                { value: 'CLAIM', label: 'Claim' },
                { value: 'SUB_CLAIM', label: 'Sub-Claim' },
              ]}
            />
            <Select
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              options={[
                { value: 'DETAIL', label: 'Detail' },
                { value: 'SUMMARY', label: 'Summary' },
              ]}
            />
          </>
        )}
        <Button onClick={run}>Preview</Button>
        <Button variant="secondary" onClick={exportExcel}>Export to Excel</Button>
      </div>
      <Table columns={columns} rows={rows} loading={loading} emptyMessage="Run a report to preview results" />
    </div>
  );
}
